import { escapeHtml } from './html';

/**
 * Criterion keys whose provider-fit cell is sourced from the Salesforce interim fields
 * rather than from MLE (BPP-265), so the column agrees with the Availability and
 * Engagement Cost sections further down the interim one-pager.
 *
 * `budget` is the internal key for the "Price" criterion type — see the type-to-key
 * mapping in the backend's salesforce.service.ts (price -> budget, geography -> location).
 */
export const SF_SOURCED_CRITERION_KEYS = new Set(['availability', 'budget']);

export type SalesforceCriterionFitSource = {
  availability?: { start?: { value?: string }; onsite?: { value?: string } };
  fees?: { value?: string };
};

export type SalesforceCriterionFit = { html: string; text: string };

/**
 * Builds the provider-fit cell for a Salesforce-sourced criterion. Returns undefined for
 * criteria that are not Salesforce-sourced, so callers can fall through to MLE content.
 *
 * Both forms are produced here rather than deriving the text from the HTML: the PDF's
 * text fallback would otherwise run the two availability lines together, since
 * htmlToPlainText reads textContent and paragraph breaks leave no separator behind.
 */
export const salesforceCriterionFit = (
  criterionKey: string,
  source: SalesforceCriterionFitSource | undefined,
): SalesforceCriterionFit | undefined => {
  if (criterionKey === 'availability') {
    // start.value already resolves an "Other" start date type to its Other Notes detail
    // on the backend (BPP-252), so this reads the real availability rather than "Other".
    const start = source?.availability?.start?.value?.trim();
    const onsite = source?.availability?.onsite?.value?.trim();

    const lines: { label: string; value: string }[] = [];
    if (start) lines.push({ label: 'Start', value: start });
    if (onsite) lines.push({ label: 'On-site', value: onsite });
    if (lines.length === 0) return { html: '<p>Not yet available</p>', text: 'Not yet available' };

    return {
      html: lines.map((l) => `<p><strong>${l.label}:</strong> ${escapeHtml(l.value)}</p>`).join(''),
      text: lines.map((l) => `${l.label}: ${l.value}`).join('\n'),
    };
  }

  if (criterionKey === 'budget') {
    const rate = source?.fees?.value?.trim();
    if (!rate) return { html: '<p>Rate not yet available</p>', text: 'Rate not yet available' };
    return { html: `<p>${escapeHtml(rate)}</p>`, text: rate };
  }

  return undefined;
};

/**
 * Salesforce supplies these two cells only on interim one-pagers, and only until someone
 * edits the cell by hand. Records generated before BPP-265 carry MLE text with no
 * `explanation_source`, so they correctly defer to Salesforce.
 */
export const shouldUseSalesforceCriterionFit = (args: {
  onePagerType: string;
  criterionKey: string;
  explanationSource?: string;
}): boolean =>
  args.onePagerType === 'interim' &&
  SF_SOURCED_CRITERION_KEYS.has(args.criterionKey) &&
  args.explanationSource !== 'user';
